<template>
  <div class="sensor-groups-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Sensor Groups</h1>
          <p>Gestión de grupos de sensores</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="sensorGroupsList"
            :loading="loading"
            class="elevation-1"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Grupos de Sensores</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Grupo
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.name="{ item }">
              <div class="d-flex align-center">
                <v-icon class="mr-2" color="primary">mdi-folder-multiple</v-icon>
                <span class="font-weight-medium">{{ item.name }}</span>
              </div>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.updatedAt="{ item }">
              {{ formatDate(item.updatedAt) }}
            </template>
            <template v-slot:item.createdBy="{ item }">
              <v-chip color="blue" variant="outlined" small>
                {{ item.createdBy }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn size="small" icon variant="text" @click="viewSensors(item)">
                <v-icon>mdi-eye</v-icon>
                <v-tooltip activator="parent" location="top">
                  Ver sensores del grupo
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="editItem(item)">
                <v-icon>mdi-pencil</v-icon>
                <v-tooltip activator="parent" location="top">
                  Editar grupo
                </v-tooltip>
              </v-btn>
              <v-btn size="small" icon variant="text" @click="deleteItem(item)">
                <v-icon>mdi-delete</v-icon>
                <v-tooltip activator="parent" location="top">
                  Eliminar grupo
                </v-tooltip>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>

      <!-- Dialog para crear/editar grupo -->
      <v-dialog v-model="dialog" max-width="500px">
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ editedIndex === -1 ? 'Nuevo Grupo' : 'Editar Grupo' }}</span>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="editedItem.name"
                    label="Nombre del Grupo"
                    required
                    :rules="nameRules"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="close">
              Cancelar
            </v-btn>
            <v-btn color="blue-darken-1" variant="text" @click="save">
              Guardar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog de confirmación para eliminar -->
      <v-dialog v-model="deleteDialog" max-width="400px">
        <v-card>
          <v-card-title class="text-h5">¿Confirmar eliminación?</v-card-title>
          <v-card-text>
            ¿Estás seguro de que quieres eliminar el grupo "{{ itemToDelete?.name }}"?
            Esta acción no se puede deshacer.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="blue-darken-1" variant="text" @click="deleteDialog = false">
              Cancelar
            </v-btn>
            <v-btn color="red-darken-1" variant="text" @click="confirmDelete">
              Eliminar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from 'vue'
import { useSensorGroupStore } from './store'
import { SensorGroup } from './models/sensorGroup'

const sensorGroupStore = useSensorGroupStore()
const loading = ref(false)
const dialog = ref(false)
const deleteDialog = ref(false)
const editedIndex = ref(-1)
const editedItem = ref(new SensorGroup())
const defaultItem = new SensorGroup()
const itemToDelete = ref<SensorGroup | null>(null)

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'Nombre', key: 'name' },
  { title: 'Creado', key: 'createdAt' },
  { title: 'Actualizado', key: 'updatedAt' },
  { title: 'Creado por', key: 'createdBy' },
  { title: 'Acciones', key: 'actions', sortable: false, width: '150px' },
]

const nameRules = [
  (v: string) => !!v || 'El nombre es requerido',
  (v: string) => (v && v.length >= 2) || 'El nombre debe tener al menos 2 caracteres',
]

const sensorGroupsList = computed(() => sensorGroupStore.sensorGroups)

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const refreshData = async () => {
  loading.value = true
  try {
    await sensorGroupStore.getSensorGroups()
  } finally {
    loading.value = false
  }
}

const addItem = () => {
  editedIndex.value = -1
  editedItem.value = Object.assign({}, defaultItem)
  dialog.value = true
}

const editItem = (item: SensorGroup) => {
  editedIndex.value = sensorGroupsList.value.indexOf(item)
  editedItem.value = Object.assign({}, item)
  dialog.value = true
}

const deleteItem = (item: SensorGroup) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const viewSensors = (item: SensorGroup) => {
  // Navegar a la vista de sensores del grupo o mostrar un dialog con los sensores
  console.log('View sensors for group:', item.name)
  // Aquí podrías navegar a una ruta como /sensor-groups/:id/sensors
  // o mostrar un dialog con la lista de sensores del grupo
}

const close = () => {
  dialog.value = false
  nextTick(() => {
    editedItem.value = Object.assign({}, defaultItem)
    editedIndex.value = -1
  })
}

const save = async () => {
  try {
    if (editedIndex.value > -1) {
      // Editar grupo existente
      await sensorGroupStore.putSensorGroup(editedItem.value)
    } else {
      // Crear nuevo grupo
      await sensorGroupStore.postSensorGroup(editedItem.value)
    }
    await refreshData()
    close()
  } catch (error) {
    console.error('Error saving sensor group:', error)
  }
}

const confirmDelete = async () => {
  if (itemToDelete.value) {
    try {
      await sensorGroupStore.deleteSensorGroup(itemToDelete.value.id)
      await refreshData()
    } catch (error) {
      console.error('Error deleting sensor group:', error)
    }
  }
  deleteDialog.value = false
  itemToDelete.value = null
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.sensor-groups-view {
  height: 100%;
}
</style>
